import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  Check,
  Eye,
  GripVertical,
  Languages,
  Plus,
  Save,
  Send,
  Trash2,
  UtensilsCrossed,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { MenuDraft, MenuLanguage, MenuSliderEntry } from "@qassist/schemas";
import { emptyMenuDraft, menuPublishIssues, normalizeMenuSlug } from "@qassist/schemas";
import { api } from "./api.js";
import { MenuRenderer } from "./MenuRenderer.js";
import { menuThemes } from "./menuThemes.js";

interface MenuRecord {
  id: string;
  slug: string;
  slugLocked: boolean;
  draft: MenuDraft;
  revision: number;
  publishedVersion: number;
  publishedAt: string | null;
}
interface MenuWorkspaceResponse {
  menu: MenuRecord | null;
  emptyDraft: MenuDraft;
  versions: Array<{ id: string; version: number; publishedAt: string; snapshot: MenuDraft }>;
  translationAvailable: boolean;
}
interface ClientAsset {
  id: string;
  name: string;
  mimeType: string;
  publicUrl: string;
}
type WorkspaceTab = "profile" | "home" | "categories" | "themes" | "preview" | "publish";

const workspaceTabs: Array<{ id: WorkspaceTab; label: string }> = [
  { id: "profile", label: "Restaurant" },
  { id: "home", label: "Home slider" },
  { id: "categories", label: "Categories & items" },
  { id: "themes", label: "Themes" },
  { id: "preview", label: "Preview" },
  { id: "publish", label: "Publish & history" },
];

function isWorkspaceTab(value: string | null): value is WorkspaceTab {
  return workspaceTabs.some((entry) => entry.id === value);
}

function itemCountLabel(count: number): string {
  return `${count} ${count === 1 ? "item" : "items"}`;
}

function move<T>(items: T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length || from === to) return items;
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

function ImageSelect({
  value,
  assets,
  label,
  onChange,
}: {
  value: string | null;
  assets: ClientAsset[];
  label: string;
  onChange(value: string | null): void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={value ?? ""} onChange={(event) => onChange(event.target.value || null)}>
        <option value="">No image</option>
        {assets.map((asset) => (
          <option key={asset.id} value={asset.id}>
            {asset.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function BilingualInput({
  label,
  value,
  multiline = false,
  revision,
  translationAvailable,
  translationDisabled,
  onChange,
}: {
  label: string;
  value: { ar: string; en: string };
  multiline?: boolean;
  revision: number;
  translationAvailable: boolean;
  translationDisabled: boolean;
  onChange(value: { ar: string; en: string }): void;
}) {
  const [suggestion, setSuggestion] = useState<{
    id: string;
    source: string;
    target: MenuLanguage;
    text: string;
  } | null>(null);
  const [busy, setBusy] = useState<MenuLanguage | null>(null);
  const [error, setError] = useState("");
  async function translate(target: MenuLanguage) {
    const source: MenuLanguage = target === "ar" ? "en" : "ar";
    if (!value[source].trim()) return;
    setBusy(target);
    setError("");
    setSuggestion(null);
    try {
      const result = await api<{ suggestionId: string; translation: string }>(
        "/api/v1/client/menu/translate",
        {
          method: "POST",
          body: JSON.stringify({
            sourceLanguage: source,
            targetLanguage: target,
            sourceText: value[source],
            currentTargetText: value[target],
            draftRevision: revision,
          }),
        },
      );
      setSuggestion({
        id: result.suggestionId,
        source: value[source],
        target,
        text: result.translation,
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Translation failed");
    } finally {
      setBusy(null);
    }
  }
  async function accept() {
    if (!suggestion) return;
    if (value[suggestion.target].trim() && !window.confirm("Replace the existing translation?"))
      return;
    if (value[suggestion.target === "ar" ? "en" : "ar"] !== suggestion.source) {
      setError("The source changed. Request a new translation.");
      return;
    }
    setBusy(suggestion.target);
    try {
      const accepted = await api<{ translation: string }>(
        "/api/v1/client/menu/translations/accept",
        {
          method: "POST",
          body: JSON.stringify({
            suggestionId: suggestion.id,
            sourceText: suggestion.source,
            draftRevision: revision,
          }),
        },
      );
      onChange({ ...value, [suggestion.target]: suggestion.text || accepted.translation });
      setSuggestion(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Translation expired");
    } finally {
      setBusy(null);
    }
  }
  const Control = multiline ? "textarea" : "input";
  const unavailableReason = !translationAvailable
    ? "AI translation is unavailable for this deployment."
    : translationDisabled
      ? "Save the current draft before requesting AI translation."
      : undefined;
  return (
    <fieldset className="menu-bilingual">
      <legend>{label}</legend>
      <div className="menu-bilingual__fields">
        <label className="field">
          <span>العربية</span>
          <Control
            dir="rtl"
            value={value.ar}
            onChange={(event) => onChange({ ...value, ar: event.target.value })}
          />
          <button
            type="button"
            className="button button--quiet"
            disabled={
              !translationAvailable || translationDisabled || busy !== null || !value.en.trim()
            }
            title={unavailableReason}
            onClick={() => void translate("ar")}
          >
            <Languages size={15} />
            {busy === "ar" ? "Translating…" : "AI → Arabic"}
          </button>
        </label>
        <label className="field">
          <span>English</span>
          <Control
            dir="ltr"
            value={value.en}
            onChange={(event) => onChange({ ...value, en: event.target.value })}
          />
          <button
            type="button"
            className="button button--quiet"
            disabled={
              !translationAvailable || translationDisabled || busy !== null || !value.ar.trim()
            }
            title={unavailableReason}
            onClick={() => void translate("en")}
          >
            <Languages size={15} />
            {busy === "en" ? "Translating…" : "AI → English"}
          </button>
        </label>
      </div>
      {unavailableReason ? <small className="menu-control-help">{unavailableReason}</small> : null}
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      {suggestion ? (
        <section className="menu-translation-review" aria-label="Translation review">
          <div>
            <strong>Source</strong>
            <p dir={suggestion.target === "ar" ? "ltr" : "rtl"}>{suggestion.source}</p>
          </div>
          <label>
            <strong>Suggested translation</strong>
            <textarea
              dir={suggestion.target === "ar" ? "rtl" : "ltr"}
              value={suggestion.text}
              onChange={(event) => setSuggestion({ ...suggestion, text: event.target.value })}
            />
          </label>
          <div className="menu-inline-actions">
            <button type="button" className="button button--primary" onClick={() => void accept()}>
              <Check size={16} />
              Use translation
            </button>
            <button
              type="button"
              className="button button--secondary"
              onClick={() => setSuggestion(null)}
            >
              Discard
            </button>
          </div>
        </section>
      ) : null}
    </fieldset>
  );
}

function OrderControls({
  index,
  count,
  label,
  onMove,
  onRemove,
}: {
  index: number;
  count: number;
  label: string;
  onMove(to: number): void;
  onRemove(): void;
}) {
  return (
    <div className="menu-order-controls">
      <GripVertical aria-hidden="true" />
      <button
        type="button"
        onClick={() => onMove(index - 1)}
        disabled={index === 0}
        aria-label={`Move ${label} up`}
      >
        <ArrowUp />
      </button>
      <button
        type="button"
        onClick={() => onMove(index + 1)}
        disabled={index === count - 1}
        aria-label={`Move ${label} down`}
      >
        <ArrowDown />
      </button>
      <button
        type="button"
        className="danger-icon"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
      >
        <Trash2 />
      </button>
    </div>
  );
}

function SliderEditor({
  title,
  entries,
  assets,
  revision,
  translationAvailable,
  translationDisabled,
  onChange,
}: {
  title: string;
  entries: MenuSliderEntry[];
  assets: ClientAsset[];
  revision: number;
  translationAvailable: boolean;
  translationDisabled: boolean;
  onChange(entries: MenuSliderEntry[]): void;
}) {
  const [dragged, setDragged] = useState<number | null>(null);
  return (
    <section className="menu-editor-section">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Image slider</span>
          <h2>{title}</h2>
        </div>
        <button
          type="button"
          className="button button--secondary"
          disabled={!assets.length || entries.length >= 20}
          title={
            !assets.length
              ? "Upload an image in Assets before adding a slider image."
              : entries.length >= 20
                ? "This slider already has the maximum of 20 images."
                : undefined
          }
          onClick={() =>
            onChange([
              ...entries,
              {
                id: crypto.randomUUID(),
                assetId: assets[0]!.id,
                alt: { ar: "", en: "" },
                caption: { ar: "", en: "" },
              },
            ])
          }
        >
          <Plus size={16} />
          Add image
        </button>
      </div>
      {!entries.length ? (
        <div className="empty-state compact">
          <p>
            {assets.length
              ? "No slider images. This section will be omitted from the public menu."
              : "No slider images. Upload an image asset before adding one here."}
          </p>
          {!assets.length ? (
            <Link className="text-button" to="/client/assets">
              Open Assets
            </Link>
          ) : null}
        </div>
      ) : (
        <div className="menu-editor-list">
          {entries.map((entry, index) => (
            <article
              key={entry.id}
              className="menu-editor-card"
              draggable
              onDragStart={() => setDragged(index)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => {
                if (dragged !== null) onChange(move(entries, dragged, index));
                setDragged(null);
              }}
            >
              <OrderControls
                index={index}
                count={entries.length}
                label={`slide ${index + 1}`}
                onMove={(to) => onChange(move(entries, index, to))}
                onRemove={() => onChange(entries.filter((item) => item.id !== entry.id))}
              />
              <ImageSelect
                label="Image"
                value={entry.assetId}
                assets={assets}
                onChange={(assetId) => {
                  if (assetId)
                    onChange(
                      entries.map((item) => (item.id === entry.id ? { ...item, assetId } : item)),
                    );
                }}
              />
              <BilingualInput
                label="Alternative text"
                value={entry.alt}
                revision={revision}
                translationAvailable={translationAvailable}
                translationDisabled={translationDisabled}
                onChange={(alt) =>
                  onChange(entries.map((item) => (item.id === entry.id ? { ...item, alt } : item)))
                }
              />
              <BilingualInput
                label="Optional caption"
                value={entry.caption}
                revision={revision}
                translationAvailable={translationAvailable}
                translationDisabled={translationDisabled}
                onChange={(caption) =>
                  onChange(
                    entries.map((item) => (item.id === entry.id ? { ...item, caption } : item)),
                  )
                }
              />
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export function MenuWorkspace() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const menuQuery = useQuery({
    queryKey: ["client", "menu"],
    queryFn: () => api<MenuWorkspaceResponse>("/api/v1/client/menu"),
  });
  const assetsQuery = useQuery({
    queryKey: ["client", "assets"],
    queryFn: () => api<ClientAsset[]>("/api/v1/client/assets"),
  });
  const requestedSection = searchParams.get("section");
  const tab: WorkspaceTab = isWorkspaceTab(requestedSection) ? requestedSection : "profile";
  const [draft, setDraft] = useState<MenuDraft>(() => emptyMenuDraft());
  const [baseline, setBaseline] = useState<MenuDraft>(() => emptyMenuDraft());
  const [slug, setSlug] = useState("");
  const [baselineSlug, setBaselineSlug] = useState("");
  const [revision, setRevision] = useState(0);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [previewLanguage, setPreviewLanguage] = useState<MenuLanguage>("ar");
  const [previewWidth, setPreviewWidth] = useState<"phone" | "tablet" | "desktop" | "landscape">(
    "phone",
  );
  const [notice, setNotice] = useState("");
  useEffect(() => {
    if (!menuQuery.data) return;
    const next = menuQuery.data.menu?.draft ?? menuQuery.data.emptyDraft;
    setDraft(structuredClone(next));
    setBaseline(structuredClone(next));
    setSlug(menuQuery.data.menu?.slug ?? "");
    setBaselineSlug(menuQuery.data.menu?.slug ?? "");
    setRevision(menuQuery.data.menu?.revision ?? 0);
  }, [menuQuery.data]);
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(baseline) || slug !== baselineSlug,
    [baseline, baselineSlug, draft, slug],
  );
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dirty) event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const assets = (assetsQuery.data ?? []).filter((asset) => asset.mimeType.startsWith("image/"));
  const assetUrls = Object.fromEntries(assets.map((asset) => [asset.id, asset.publicUrl]));
  const selectedCategory =
    draft.categories.find((category) => category.id === selectedCategoryId) ?? null;
  const translationAvailable = menuQuery.data?.translationAvailable ?? false;
  const saveMutation = useMutation({
    mutationFn: () =>
      api<MenuRecord>("/api/v1/client/menu", {
        method: "PUT",
        body: JSON.stringify({ revision, slug: normalizeMenuSlug(slug), draft }),
      }),
    onSuccess: (saved) => {
      setRevision(saved.revision);
      setSlug(saved.slug);
      setBaselineSlug(saved.slug);
      setDraft(structuredClone(saved.draft));
      setBaseline(structuredClone(saved.draft));
      setNotice("Draft saved.");
      void queryClient.invalidateQueries({ queryKey: ["client", "menu"] });
    },
  });
  const publishMutation = useMutation({
    mutationFn: () =>
      api<{ version: number; publishedAt: string }>("/api/v1/client/menu/publish", {
        method: "POST",
        body: JSON.stringify({ revision }),
      }),
    onSuccess: (result) => {
      setNotice(`Version ${result.version} published.`);
      void queryClient.invalidateQueries({ queryKey: ["client", "menu"] });
    },
  });
  const restoreMutation = useMutation({
    mutationFn: (version: number) =>
      api<{ version: number; revision: number; draft: MenuDraft }>(
        `/api/v1/client/menu/versions/${version}/restore`,
        { method: "POST" },
      ),
    onSuccess: (result) => {
      setRevision(result.revision);
      setDraft(result.draft);
      setBaseline(structuredClone(result.draft));
      setNotice(`Version ${result.version} published from history.`);
      void queryClient.invalidateQueries({ queryKey: ["client", "menu"] });
    },
  });
  const updateCategory = (
    id: string,
    updater: (category: MenuDraft["categories"][number]) => MenuDraft["categories"][number],
  ) =>
    setDraft((current) => ({
      ...current,
      categories: current.categories.map((category) =>
        category.id === id ? updater(category) : category,
      ),
    }));
  if (menuQuery.isPending)
    return (
      <section className="panel" aria-busy="true">
        <div className="skeleton-line" />
        <div className="skeleton-card" />
      </section>
    );
  if (menuQuery.isError || !menuQuery.data)
    return (
      <section className="panel empty-state">
        <UtensilsCrossed />
        <h2>Menu workspace unavailable</h2>
        <p>{menuQuery.error instanceof Error ? menuQuery.error.message : "Try again."}</p>
        <button className="button button--primary" onClick={() => void menuQuery.refetch()}>
          Retry
        </button>
      </section>
    );
  const saveError = saveMutation.error instanceof Error ? saveMutation.error.message : "";
  const normalizedSlug = normalizeMenuSlug(slug);
  const slugIsValid = Boolean(normalizedSlug);
  const saveDisabledReason = !dirty
    ? "No unsaved changes."
    : !slugIsValid
      ? "Enter a public slug before saving."
      : saveMutation.isPending
        ? "Saving the current draft."
        : undefined;
  return (
    <div className="menu-workspace">
      <section className="menu-workspace__hero">
        <div>
          <span className="eyebrow">Q-Assist Menu</span>
          <h2>Restaurant menu</h2>
          <p>Build one bilingual, display-only menu and publish it across all 13 themes.</p>
        </div>
        <div className="menu-workspace__status">
          <span
            className={
              menuQuery.data.menu?.publishedVersion
                ? "status-pill status-pill--success"
                : "status-pill"
            }
          >
            {menuQuery.data.menu?.publishedVersion
              ? `Published v${menuQuery.data.menu.publishedVersion}`
              : "Not published"}
          </span>
          <button
            type="button"
            className="button button--primary"
            disabled={Boolean(saveDisabledReason)}
            title={saveDisabledReason}
            onClick={() => saveMutation.mutate()}
          >
            <Save size={17} />
            {saveMutation.isPending ? "Saving…" : "Save draft"}
          </button>
          {dirty && !slugIsValid ? (
            <small className="menu-save-hint" role="status">
              Enter a public slug to save this draft.
            </small>
          ) : null}
        </div>
      </section>
      {notice ? (
        <div className="notice notice--good" role="status">
          {notice}
          <button type="button" onClick={() => setNotice("")} aria-label="Dismiss notice">
            ×
          </button>
        </div>
      ) : null}
      {saveError ? (
        <div className="notice notice--error" role="alert">
          {saveError}
        </div>
      ) : null}
      <nav className="menu-workspace__tabs" aria-label="Menu workspace sections">
        {workspaceTabs.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-current={tab === entry.id ? "page" : undefined}
            onClick={() =>
              setSearchParams((current) => {
                const next = new URLSearchParams(current);
                if (entry.id === "profile") next.delete("section");
                else next.set("section", entry.id);
                return next;
              })
            }
          >
            {entry.label}
          </button>
        ))}
      </nav>

      {tab === "profile" ? (
        <section className="panel menu-editor-section">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Identity</span>
              <h2>Restaurant profile</h2>
            </div>
          </div>
          <div className="form-grid">
            <label className="field">
              <span>Public slug</span>
              <div className="input-prefix">
                <span>/menu/</span>
                <input
                  value={slug}
                  disabled={menuQuery.data.menu?.slugLocked}
                  required={!menuQuery.data.menu?.slugLocked}
                  aria-invalid={
                    !menuQuery.data.menu?.slugLocked && dirty && !slugIsValid ? true : undefined
                  }
                  onChange={(event) => setSlug(normalizeMenuSlug(event.target.value))}
                  placeholder="restaurant-name"
                />
              </div>
              <small>
                {menuQuery.data.menu?.slugLocked
                  ? "Locked after first publication."
                  : "Required. Use lowercase letters, numbers, and hyphens; locks after publication."}
              </small>
            </label>
            <label className="field">
              <span>Default language</span>
              <select
                value={draft.profile.defaultLanguage}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    profile: {
                      ...draft.profile,
                      defaultLanguage: event.target.value as MenuLanguage,
                    },
                  })
                }
              >
                <option value="ar">العربية</option>
                <option value="en">English</option>
              </select>
            </label>
            <label className="field">
              <span>Currency</span>
              <input
                maxLength={3}
                value={draft.profile.currency}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    profile: { ...draft.profile, currency: event.target.value.toUpperCase() },
                  })
                }
              />
            </label>
            <ImageSelect
              label="Logo"
              value={draft.profile.logoAssetId}
              assets={assets}
              onChange={(logoAssetId) =>
                setDraft({ ...draft, profile: { ...draft.profile, logoAssetId } })
              }
            />
            <ImageSelect
              label="Cover image"
              value={draft.profile.coverAssetId}
              assets={assets}
              onChange={(coverAssetId) =>
                setDraft({ ...draft, profile: { ...draft.profile, coverAssetId } })
              }
            />
          </div>
          <BilingualInput
            label="Restaurant name"
            value={draft.profile.name}
            revision={revision}
            translationAvailable={translationAvailable}
            translationDisabled={dirty || !menuQuery.data.menu}
            onChange={(name) => setDraft({ ...draft, profile: { ...draft.profile, name } })}
          />
          <BilingualInput
            label="Description"
            multiline
            value={draft.profile.description}
            revision={revision}
            translationAvailable={translationAvailable}
            translationDisabled={dirty || !menuQuery.data.menu}
            onChange={(description) =>
              setDraft({ ...draft, profile: { ...draft.profile, description } })
            }
          />
          <BilingualInput
            label="Address"
            multiline
            value={draft.profile.address}
            revision={revision}
            translationAvailable={translationAvailable}
            translationDisabled={dirty || !menuQuery.data.menu}
            onChange={(address) => setDraft({ ...draft, profile: { ...draft.profile, address } })}
          />
          <div className="form-grid">
            <label className="field">
              <span>Phone</span>
              <input
                value={draft.profile.phone}
                onChange={(event) =>
                  setDraft({ ...draft, profile: { ...draft.profile, phone: event.target.value } })
                }
              />
            </label>
            <label className="field">
              <span>WhatsApp number or link</span>
              <input
                value={draft.profile.whatsapp}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    profile: { ...draft.profile, whatsapp: event.target.value },
                  })
                }
              />
            </label>
            <label className="field">
              <span>Website</span>
              <input
                type="url"
                value={draft.profile.websiteUrl}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    profile: { ...draft.profile, websiteUrl: event.target.value },
                  })
                }
              />
            </label>
            <label className="field">
              <span>Map link</span>
              <input
                type="url"
                value={draft.profile.mapUrl}
                onChange={(event) =>
                  setDraft({ ...draft, profile: { ...draft.profile, mapUrl: event.target.value } })
                }
              />
            </label>
          </div>
          <fieldset className="menu-hours">
            <legend>Opening hours</legend>
            {draft.profile.openingHours.map((entry, index) => (
              <div key={entry.day}>
                <strong>{entry.day}</strong>
                <label>
                  <input
                    type="checkbox"
                    checked={entry.closed}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        profile: {
                          ...draft.profile,
                          openingHours: draft.profile.openingHours.map((hour, position) =>
                            position === index ? { ...hour, closed: event.target.checked } : hour,
                          ),
                        },
                      })
                    }
                  />
                  Closed
                </label>
                <input
                  type="time"
                  disabled={entry.closed}
                  value={entry.opensAt}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      profile: {
                        ...draft.profile,
                        openingHours: draft.profile.openingHours.map((hour, position) =>
                          position === index ? { ...hour, opensAt: event.target.value } : hour,
                        ),
                      },
                    })
                  }
                  aria-label={`${entry.day} opens`}
                />
                <input
                  type="time"
                  disabled={entry.closed}
                  value={entry.closesAt}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      profile: {
                        ...draft.profile,
                        openingHours: draft.profile.openingHours.map((hour, position) =>
                          position === index ? { ...hour, closesAt: event.target.value } : hour,
                        ),
                      },
                    })
                  }
                  aria-label={`${entry.day} closes`}
                />
              </div>
            ))}
          </fieldset>
          <fieldset className="menu-social">
            <legend>Social links</legend>
            {draft.profile.socialLinks.map((link, index) => (
              <div key={link.id}>
                <select
                  aria-label={`Social network ${index + 1}`}
                  value={link.network}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      profile: {
                        ...draft.profile,
                        socialLinks: draft.profile.socialLinks.map((item) =>
                          item.id === link.id
                            ? { ...item, network: event.target.value as typeof link.network }
                            : item,
                        ),
                      },
                    })
                  }
                >
                  {["instagram", "facebook", "tiktok", "x", "youtube", "linkedin"].map(
                    (network) => (
                      <option key={network}>{network}</option>
                    ),
                  )}
                </select>
                <input
                  type="url"
                  value={link.url}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      profile: {
                        ...draft.profile,
                        socialLinks: draft.profile.socialLinks.map((item) =>
                          item.id === link.id ? { ...item, url: event.target.value } : item,
                        ),
                      },
                    })
                  }
                  aria-label={`Social link ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() =>
                    setDraft({
                      ...draft,
                      profile: {
                        ...draft.profile,
                        socialLinks: draft.profile.socialLinks.filter(
                          (item) => item.id !== link.id,
                        ),
                      },
                    })
                  }
                  aria-label={`Remove social link ${index + 1}`}
                >
                  <Trash2 />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="button button--secondary"
              disabled={draft.profile.socialLinks.length >= 12}
              onClick={() =>
                setDraft({
                  ...draft,
                  profile: {
                    ...draft.profile,
                    socialLinks: [
                      ...draft.profile.socialLinks,
                      { id: crypto.randomUUID(), network: "instagram", url: "" },
                    ],
                  },
                })
              }
            >
              <Plus size={16} />
              Add social link
            </button>
          </fieldset>
        </section>
      ) : null}

      {tab === "home" ? (
        <SliderEditor
          title="Homepage slider"
          entries={draft.homeSlides}
          assets={assets}
          revision={revision}
          translationAvailable={translationAvailable}
          translationDisabled={dirty || !menuQuery.data.menu}
          onChange={(homeSlides) => setDraft({ ...draft, homeSlides })}
        />
      ) : null}

      {tab === "categories" ? (
        <div className="menu-category-workspace">
          <aside className="panel menu-category-list">
            <div className="section-heading">
              <div>
                <span className="eyebrow">Structure</span>
                <h2>Categories</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                aria-label="Add category"
                disabled={draft.categories.length >= 100}
                onClick={() => {
                  const id = crypto.randomUUID();
                  setDraft({
                    ...draft,
                    categories: [
                      ...draft.categories,
                      {
                        id,
                        name: { ar: "", en: "" },
                        description: { ar: "", en: "" },
                        coverAssetId: null,
                        visible: true,
                        slides: [],
                        items: [],
                      },
                    ],
                  });
                  setSelectedCategoryId(id);
                }}
              >
                <Plus />
              </button>
            </div>
            {!draft.categories.length ? (
              <div className="empty-state compact">
                <p>Add the first category to begin.</p>
              </div>
            ) : (
              draft.categories.map((category, index) => (
                <div
                  key={category.id}
                  className={`menu-category-row ${selectedCategoryId === category.id ? "is-active" : ""}`}
                  draggable
                  onDragOver={(event) => event.preventDefault()}
                  onDragStart={(event) => event.dataTransfer.setData("text/plain", String(index))}
                  onDrop={(event) =>
                    setDraft({
                      ...draft,
                      categories: move(
                        draft.categories,
                        Number(event.dataTransfer.getData("text/plain")),
                        index,
                      ),
                    })
                  }
                >
                  <button type="button" onClick={() => setSelectedCategoryId(category.id)}>
                    <strong>
                      {category.name.en || category.name.ar || `Category ${index + 1}`}
                    </strong>
                    <small>
                      {itemCountLabel(category.items.length)} ·{" "}
                      {category.visible ? "Visible" : "Hidden"}
                    </small>
                  </button>
                  <OrderControls
                    index={index}
                    count={draft.categories.length}
                    label={`category ${index + 1}`}
                    onMove={(to) =>
                      setDraft({ ...draft, categories: move(draft.categories, index, to) })
                    }
                    onRemove={() => {
                      if (window.confirm("Remove this category and all its items?")) {
                        setDraft({
                          ...draft,
                          categories: draft.categories.filter((entry) => entry.id !== category.id),
                        });
                        if (selectedCategoryId === category.id) setSelectedCategoryId(null);
                      }
                    }}
                  />
                </div>
              ))
            )}
          </aside>
          {selectedCategory ? (
            <section className="panel menu-editor-section">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Selected category</span>
                  <h2>
                    {selectedCategory.name.en ||
                      selectedCategory.name.ar ||
                      `Category ${draft.categories.indexOf(selectedCategory) + 1}`}
                  </h2>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={selectedCategory.visible}
                    onChange={(event) =>
                      updateCategory(selectedCategory.id, (category) => ({
                        ...category,
                        visible: event.target.checked,
                      }))
                    }
                  />
                  <span>Visible</span>
                </label>
              </div>
              <BilingualInput
                label="Category name"
                value={selectedCategory.name}
                revision={revision}
                translationAvailable={translationAvailable}
                translationDisabled={dirty || !menuQuery.data.menu}
                onChange={(name) =>
                  updateCategory(selectedCategory.id, (category) => ({ ...category, name }))
                }
              />
              <BilingualInput
                label="Description"
                multiline
                value={selectedCategory.description}
                revision={revision}
                translationAvailable={translationAvailable}
                translationDisabled={dirty || !menuQuery.data.menu}
                onChange={(description) =>
                  updateCategory(selectedCategory.id, (category) => ({ ...category, description }))
                }
              />
              <ImageSelect
                label="Cover image"
                value={selectedCategory.coverAssetId}
                assets={assets}
                onChange={(coverAssetId) =>
                  updateCategory(selectedCategory.id, (category) => ({ ...category, coverAssetId }))
                }
              />
              <SliderEditor
                title="Category slider"
                entries={selectedCategory.slides}
                assets={assets}
                revision={revision}
                translationAvailable={translationAvailable}
                translationDisabled={dirty || !menuQuery.data.menu}
                onChange={(slides) =>
                  updateCategory(selectedCategory.id, (category) => ({ ...category, slides }))
                }
              />
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Display order</span>
                  <h2>Menu items</h2>
                </div>
                <button
                  type="button"
                  className="button button--secondary"
                  disabled={selectedCategory.items.length >= 500}
                  onClick={() =>
                    updateCategory(selectedCategory.id, (category) => ({
                      ...category,
                      items: [
                        ...category.items,
                        {
                          id: crypto.randomUUID(),
                          name: { ar: "", en: "" },
                          description: { ar: "", en: "" },
                          imageAssetId: null,
                          price: "0",
                          visible: true,
                          variants: [],
                        },
                      ],
                    }))
                  }
                >
                  <Plus size={16} />
                  Add item
                </button>
              </div>
              {!selectedCategory.items.length ? (
                <div className="empty-state compact">
                  <p>No items in this category.</p>
                </div>
              ) : (
                <div className="menu-editor-list">
                  {selectedCategory.items.map((item, itemIndex) => (
                    <article className="menu-editor-card" key={item.id}>
                      <div className="menu-item-summary">
                        <span>
                          <strong>{item.name.en || item.name.ar || `Item ${itemIndex + 1}`}</strong>
                          <small>
                            {item.price} {draft.profile.currency} ·{" "}
                            {item.visible ? "Visible" : "Hidden"}
                          </small>
                        </span>
                        <OrderControls
                          index={itemIndex}
                          count={selectedCategory.items.length}
                          label={`item ${itemIndex + 1}`}
                          onMove={(to) =>
                            updateCategory(selectedCategory.id, (category) => ({
                              ...category,
                              items: move(category.items, itemIndex, to),
                            }))
                          }
                          onRemove={() => {
                            if (window.confirm("Remove this menu item?"))
                              updateCategory(selectedCategory.id, (category) => ({
                                ...category,
                                items: category.items.filter((entry) => entry.id !== item.id),
                              }));
                          }}
                        />
                      </div>
                      <div className="menu-item-fields">
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={item.visible}
                            onChange={(event) =>
                              updateCategory(selectedCategory.id, (category) => ({
                                ...category,
                                items: category.items.map((entry) =>
                                  entry.id === item.id
                                    ? { ...entry, visible: event.target.checked }
                                    : entry,
                                ),
                              }))
                            }
                          />
                          <span>Visible</span>
                        </label>
                        <BilingualInput
                          label="Item name"
                          value={item.name}
                          revision={revision}
                          translationAvailable={translationAvailable}
                          translationDisabled={dirty || !menuQuery.data.menu}
                          onChange={(name) =>
                            updateCategory(selectedCategory.id, (category) => ({
                              ...category,
                              items: category.items.map((entry) =>
                                entry.id === item.id ? { ...entry, name } : entry,
                              ),
                            }))
                          }
                        />
                        <BilingualInput
                          label="Description"
                          multiline
                          value={item.description}
                          revision={revision}
                          translationAvailable={translationAvailable}
                          translationDisabled={dirty || !menuQuery.data.menu}
                          onChange={(description) =>
                            updateCategory(selectedCategory.id, (category) => ({
                              ...category,
                              items: category.items.map((entry) =>
                                entry.id === item.id ? { ...entry, description } : entry,
                              ),
                            }))
                          }
                        />
                        <div className="form-grid">
                          <label className="field">
                            <span>Base price</span>
                            <input
                              inputMode="decimal"
                              value={item.price}
                              onChange={(event) =>
                                updateCategory(selectedCategory.id, (category) => ({
                                  ...category,
                                  items: category.items.map((entry) =>
                                    entry.id === item.id
                                      ? { ...entry, price: event.target.value }
                                      : entry,
                                  ),
                                }))
                              }
                            />
                          </label>
                          <ImageSelect
                            label="Item image"
                            value={item.imageAssetId}
                            assets={assets}
                            onChange={(imageAssetId) =>
                              updateCategory(selectedCategory.id, (category) => ({
                                ...category,
                                items: category.items.map((entry) =>
                                  entry.id === item.id ? { ...entry, imageAssetId } : entry,
                                ),
                              }))
                            }
                          />
                        </div>
                        <fieldset className="menu-variants">
                          <legend>Optional variants</legend>
                          {item.variants.map((variant, variantIndex) => (
                            <div className="menu-variant" key={variant.id}>
                              <OrderControls
                                index={variantIndex}
                                count={item.variants.length}
                                label={`variant ${variantIndex + 1}`}
                                onMove={(to) =>
                                  updateCategory(selectedCategory.id, (category) => ({
                                    ...category,
                                    items: category.items.map((entry) =>
                                      entry.id === item.id
                                        ? {
                                            ...entry,
                                            variants: move(entry.variants, variantIndex, to),
                                          }
                                        : entry,
                                    ),
                                  }))
                                }
                                onRemove={() =>
                                  updateCategory(selectedCategory.id, (category) => ({
                                    ...category,
                                    items: category.items.map((entry) =>
                                      entry.id === item.id
                                        ? {
                                            ...entry,
                                            variants: entry.variants.filter(
                                              (candidate) => candidate.id !== variant.id,
                                            ),
                                          }
                                        : entry,
                                    ),
                                  }))
                                }
                              />
                              <BilingualInput
                                label={`Variant ${variantIndex + 1}`}
                                value={variant.name}
                                revision={revision}
                                translationAvailable={translationAvailable}
                                translationDisabled={dirty || !menuQuery.data.menu}
                                onChange={(name) =>
                                  updateCategory(selectedCategory.id, (category) => ({
                                    ...category,
                                    items: category.items.map((entry) =>
                                      entry.id === item.id
                                        ? {
                                            ...entry,
                                            variants: entry.variants.map((candidate) =>
                                              candidate.id === variant.id
                                                ? { ...candidate, name }
                                                : candidate,
                                            ),
                                          }
                                        : entry,
                                    ),
                                  }))
                                }
                              />
                              <label className="field">
                                <span>Final price</span>
                                <input
                                  value={variant.price}
                                  inputMode="decimal"
                                  onChange={(event) =>
                                    updateCategory(selectedCategory.id, (category) => ({
                                      ...category,
                                      items: category.items.map((entry) =>
                                        entry.id === item.id
                                          ? {
                                              ...entry,
                                              variants: entry.variants.map((candidate) =>
                                                candidate.id === variant.id
                                                  ? { ...candidate, price: event.target.value }
                                                  : candidate,
                                              ),
                                            }
                                          : entry,
                                      ),
                                    }))
                                  }
                                />
                              </label>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="button button--secondary"
                            disabled={item.variants.length >= 30}
                            onClick={() =>
                              updateCategory(selectedCategory.id, (category) => ({
                                ...category,
                                items: category.items.map((entry) =>
                                  entry.id === item.id
                                    ? {
                                        ...entry,
                                        variants: [
                                          ...entry.variants,
                                          {
                                            id: crypto.randomUUID(),
                                            name: { ar: "", en: "" },
                                            price: entry.price,
                                          },
                                        ],
                                      }
                                    : entry,
                                ),
                              }))
                            }
                          >
                            <Plus size={16} />
                            Add variant
                          </button>
                        </fieldset>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          ) : (
            <section className="panel empty-state">
              <Eye />
              <h2>Select a category</h2>
              <p>Choose a category to edit its slider and menu items.</p>
            </section>
          )}
        </div>
      ) : null}

      {tab === "themes" ? (
        <section className="panel menu-editor-section">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Presentation only</span>
              <h2>Choose from 13 themes</h2>
              <p>The same saved menu data is reused instantly in every theme.</p>
            </div>
          </div>
          <div className="menu-theme-grid">
            {menuThemes.map((theme) => (
              <button
                type="button"
                key={theme.key}
                className={draft.profile.themeKey === theme.key ? "is-selected" : ""}
                aria-pressed={draft.profile.themeKey === theme.key}
                onClick={() =>
                  setDraft({ ...draft, profile: { ...draft.profile, themeKey: theme.key } })
                }
              >
                <span className="menu-theme-swatch" data-theme={theme.key}>
                  <i />
                  <i />
                  <i />
                </span>
                <strong>{theme.name.en}</strong>
                <b dir="rtl">{theme.name.ar}</b>
                <small>{theme.description.en}</small>
                {draft.profile.themeKey === theme.key ? <Check /> : null}
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {tab === "preview" ? (
        <section className="panel menu-preview-workspace">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Draft preview</span>
              <h2>Responsive menu preview</h2>
            </div>
            <div className="menu-inline-actions">
              <select
                aria-label="Preview language"
                value={previewLanguage}
                onChange={(event) => setPreviewLanguage(event.target.value as MenuLanguage)}
              >
                <option value="ar">العربية</option>
                <option value="en">English</option>
              </select>
              <select
                aria-label="Preview size"
                value={previewWidth}
                onChange={(event) => setPreviewWidth(event.target.value as typeof previewWidth)}
              >
                <option value="phone">Phone</option>
                <option value="tablet">Tablet</option>
                <option value="desktop">Desktop</option>
                <option value="landscape">Landscape display</option>
              </select>
            </div>
          </div>
          <div className={`menu-preview-frame menu-preview-frame--${previewWidth}`}>
            <MenuRenderer
              menu={{
                ...draft,
                categories: draft.categories
                  .filter((category) => category.visible)
                  .map((category) => ({
                    ...category,
                    items: category.items.filter((item) => item.visible),
                  })),
              }}
              language={previewLanguage}
              assetUrls={assetUrls}
              route={{}}
              interactive={false}
            />
          </div>
        </section>
      ) : null}

      {tab === "publish" ? (
        <section className="panel menu-editor-section">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Atomic publication</span>
              <h2>Publish and history</h2>
              <p>Only a validated immutable version is visible at the public URL.</p>
            </div>
            <button
              type="button"
              className="button button--primary"
              disabled={
                dirty ||
                !menuQuery.data.menu ||
                publishMutation.isPending ||
                menuPublishIssues(draft).length > 0
              }
              onClick={() => publishMutation.mutate()}
            >
              <Send size={17} />
              {publishMutation.isPending ? "Publishing…" : "Publish draft"}
            </button>
          </div>
          {dirty ? <div className="notice warning">Save the draft before publishing.</div> : null}
          {menuPublishIssues(draft).length ? (
            <div className="notice notice--error" role="alert">
              <strong>Complete these fields before publishing:</strong>
              <ul>
                {menuPublishIssues(draft)
                  .slice(0, 8)
                  .map((issue) => (
                    <li key={issue}>{issue}</li>
                  ))}
              </ul>
            </div>
          ) : null}
          {publishMutation.error ? (
            <div className="notice notice--error" role="alert">
              {publishMutation.error instanceof Error
                ? publishMutation.error.message
                : "Publication failed"}
            </div>
          ) : null}
          {menuQuery.data.menu?.slug ? (
            <a
              className="button button--secondary"
              href={`/menu/${menuQuery.data.menu.slug}`}
              target="_blank"
              rel="noreferrer"
            >
              <Eye size={16} />
              Open public menu
            </a>
          ) : null}
          <div className="menu-version-list">
            <h3>Published versions</h3>
            {!menuQuery.data.versions.length ? (
              <div className="empty-state compact">
                <p>No versions published yet.</p>
              </div>
            ) : (
              menuQuery.data.versions.map((version) => (
                <article key={version.id}>
                  <div>
                    <strong>Version {version.version}</strong>
                    <small>{new Date(version.publishedAt).toLocaleString()}</small>
                    <span>{version.snapshot.profile.themeKey}</span>
                  </div>
                  <button
                    type="button"
                    className="button button--secondary"
                    disabled={
                      restoreMutation.isPending ||
                      version.version === menuQuery.data.menu?.publishedVersion
                    }
                    onClick={() => {
                      if (
                        window.confirm(
                          `Restore version ${version.version} and publish it as a new version?`,
                        )
                      )
                        restoreMutation.mutate(version.version);
                    }}
                  >
                    Restore
                  </button>
                </article>
              ))
            )}
          </div>
        </section>
      ) : null}
    </div>
  );
}
